import { cn } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { formatEur, formatNumber } from '@/lib/format-es';
import { STORE_STATUS } from '@/lib/store-status';
import {
    ChevronDown, ExternalLink, Star, MessageSquare,
    Trash2, Edit3, CheckCircle, XCircle,
    Image as ImageIcon,
} from 'lucide-react';
import type { MaterialItem } from '../types';
import { CATEGORY_CONFIG, WATER_TYPE_LABELS, SOURCE_LABELS, shortDate, longDate } from '../constants';
import { StoreMaterialEdit } from './StoreMaterialEdit';
import { StoreCommentsPanel } from './StoreCommentsPanel';

interface StoreMaterialCardProps {
    material: MaterialItem;
    isExpanded: boolean;
    isEditing: boolean;
    onToggle: () => void;
    onEdit: () => void;
    onApprove: () => void;
    onReject: () => void;
    onDelete: () => void;
    onSaveEdit: (data: Record<string, unknown>) => void;
    isActioning: boolean;
}

export function StoreMaterialCard({
    material, isExpanded, isEditing,
    onToggle, onEdit, onApprove, onReject, onDelete,
    onSaveEdit, isActioning
}: StoreMaterialCardProps) {
    const materialStatus = material.status in STORE_STATUS ? material.status : 'pending';
    const categoryCfg = CATEGORY_CONFIG[material.category];
    const waterCfg = WATER_TYPE_LABELS[material.water_type];
    const createdDate = new Date(material.created_at);
    const images = Array.isArray(material.images) ? material.images : [];
    const thumbnailUrl = images.length > 0 ? images[0] : null;

    return (
        <div className={cn(
            'rounded-xl border bg-card transition-all',
            material.status === 'pending' ? 'border-amber-500/30' : 'border-border',
            isExpanded && 'ring-1 ring-primary/20'
        )}>
            {/* Header Row */}
            <div
                className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-muted/20 transition-colors"
                onClick={onToggle}
            >
                {/* Thumbnail */}
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted/50 shrink-0 border border-border/50">
                    {thumbnailUrl ? (
                        <img
                            src={thumbnailUrl}
                            alt={material.title}
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon size={20} className="text-muted-foreground/30" />
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-foreground truncate max-w-lg">
                            {material.title}
                        </p>
                        <StatusBadge status={materialStatus} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                        {categoryCfg && (
                            <span className={cn('flex items-center gap-1', categoryCfg.color)}>
                                {categoryCfg.icon} {categoryCfg.label}
                            </span>
                        )}
                        <span className="opacity-40">·</span>
                        <span>{SOURCE_LABELS[material.source] || material.source}</span>
                        {waterCfg && (
                            <>
                                <span className="opacity-40">·</span>
                                <span className="flex items-center gap-1">{waterCfg.icon} {waterCfg.label}</span>
                            </>
                        )}
                        <span className="opacity-40">·</span>
                        <span>{shortDate(createdDate)}</span>
                    </div>
                </div>

                {/* Right side metrics */}
                <div className="hidden md:flex items-center gap-4 shrink-0">
                    {material.price && (
                        <div className="text-right">
                            <p className="text-sm font-bold text-foreground">
                                {formatEur(Number(material.price))}
                            </p>
                            {material.original_price && Number(material.original_price) > 0 && Number(material.original_price) !== Number(material.price) && (
                                <p className="text-[10px] text-muted-foreground line-through">
                                    {formatEur(Number(material.original_price))}
                                </p>
                            )}
                        </div>
                    )}

                    {material.external_rating && (
                        <div className="flex items-center gap-1 text-xs text-amber-400">
                            <Star size={12} className="fill-current" />
                            <span className="font-semibold">{formatNumber(Number(material.external_rating), 1)}</span>
                            {material.external_reviews_count && (
                                <span className="text-muted-foreground">
                                    ({formatNumber(Number(material.external_reviews_count))})
                                </span>
                            )}
                        </div>
                    )}

                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {material.comments_count > 0 && (
                            <span className="flex items-center gap-1">
                                <MessageSquare size={12} /> {material.comments_count}
                            </span>
                        )}
                        {material.ratings_count > 0 && (
                            <span className="flex items-center gap-1">
                                <Star size={12} /> {material.ratings_count}
                            </span>
                        )}
                    </div>
                </div>

                {/* Expand arrow */}
                <ChevronDown
                    size={16}
                    className={cn(
                        'text-muted-foreground transition-transform shrink-0',
                        isExpanded && 'rotate-180'
                    )}
                />
            </div>

            {/* Expanded Details */}
            {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-4">
                    {/* Images Gallery */}
                    {images.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                            {images.slice(0, 8).map((img, i) => (
                                <img
                                    key={i}
                                    src={img}
                                    alt={`${material.title} ${i + 1}`}
                                    className="w-20 h-20 rounded-lg object-cover border border-border/50 shrink-0 hover:ring-2 hover:ring-primary/30 transition-all cursor-pointer"
                                    referrerPolicy="no-referrer"
                                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                            ))}
                            {images.length > 8 && (
                                <div className="w-20 h-20 rounded-lg border border-dashed border-border flex items-center justify-center shrink-0">
                                    <span className="text-xs text-muted-foreground">+{images.length - 8}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Description */}
                    {material.description && (
                        <div className="rounded-lg bg-muted/30 p-3">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Descripción</p>
                            <p className="text-sm text-foreground whitespace-pre-line">{material.description}</p>
                        </div>
                    )}

                    {/* Recommendation Reason */}
                    {material.recommendation_reason && (
                        <div className="rounded-lg bg-primary/5 border border-primary/10 p-3">
                            <p className="text-xs font-semibold text-primary/70 uppercase tracking-wider mb-1">
                                💬 Razón de Recomendación
                            </p>
                            <p className="text-sm text-foreground whitespace-pre-line">{material.recommendation_reason}</p>
                        </div>
                    )}

                    {/* Comments Panel */}
                    {material.comments_count > 0 && (
                        <StoreCommentsPanel
                            materialId={material.id}
                            materialTitle={material.title}
                            commentsCount={material.comments_count}
                        />
                    )}

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                        <div>
                            <p className="text-muted-foreground/60 uppercase tracking-wider font-semibold">Recomendado por</p>
                            <p className="text-foreground font-medium">{material.suggested_by_name || 'Desconocido'}</p>
                        </div>
                        {material.approved_by_name && (
                            <div>
                                <p className="text-muted-foreground/60 uppercase tracking-wider font-semibold">Aprobado por</p>
                                <p className="text-foreground font-medium">{material.approved_by_name}</p>
                            </div>
                        )}
                        <div>
                            <p className="text-muted-foreground/60 uppercase tracking-wider font-semibold">Fuente</p>
                            <p className="text-foreground">{SOURCE_LABELS[material.source] || material.source}</p>
                        </div>
                        {material.seller_name && (
                            <div>
                                <p className="text-muted-foreground/60 uppercase tracking-wider font-semibold">Vendedor</p>
                                <p className="text-foreground">{material.seller_name}</p>
                            </div>
                        )}
                        <div>
                            <p className="text-muted-foreground/60 uppercase tracking-wider font-semibold">Imágenes</p>
                            <p className="text-foreground">{images.length} foto{images.length !== 1 ? 's' : ''}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground/60 uppercase tracking-wider font-semibold">Creado</p>
                            <p className="text-foreground">{longDate(createdDate)}</p>
                        </div>
                        {material.product_id && (
                            <div>
                                <p className="text-muted-foreground/60 uppercase tracking-wider font-semibold">Product ID</p>
                                <p className="text-foreground font-mono text-[10px]">{material.product_id}</p>
                            </div>
                        )}
                        {material.rejection_reason && (
                            <div className="col-span-2">
                                <p className="text-red-400/70 uppercase tracking-wider font-semibold">Razón de Rechazo</p>
                                <p className="text-red-400">{material.rejection_reason}</p>
                            </div>
                        )}
                    </div>

                    {/* Edit Form */}
                    {isEditing && (
                        <StoreMaterialEdit
                            material={material}
                            onSave={onSaveEdit}
                            onCancel={() => onEdit()}
                            isSaving={false}
                        />
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 flex-wrap">
                        {material.affiliate_link && (
                            <a
                                href={material.affiliate_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                                onClick={e => e.stopPropagation()}
                            >
                                <ExternalLink size={14} />
                                Ver Producto
                            </a>
                        )}

                        {!isEditing && (
                            <button
                                onClick={e => { e.stopPropagation(); onEdit(); }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            >
                                <Edit3 size={14} />
                                Editar
                            </button>
                        )}

                        {material.status !== 'approved' && (
                            <button
                                onClick={e => { e.stopPropagation(); onApprove(); }}
                                disabled={isActioning}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                            >
                                <CheckCircle size={14} />
                                Aprobar
                            </button>
                        )}

                        {material.status !== 'rejected' && (
                            <button
                                onClick={e => { e.stopPropagation(); onReject(); }}
                                disabled={isActioning}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                            >
                                <XCircle size={14} />
                                Rechazar
                            </button>
                        )}

                        <button
                            onClick={e => { e.stopPropagation(); onDelete(); }}
                            disabled={isActioning}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50 ml-auto"
                        >
                            <Trash2 size={14} />
                            Eliminar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
